import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import client from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";

export default function Profile() {
  const { user, updateUser } = useAuth();
  const { addToast } = useToast();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  const [twoFAEnabled, setTwoFAEnabled] = useState(user?.twoFactorEnabled ?? false);

  useEffect(() => {
    setTwoFAEnabled(user?.twoFactorEnabled ?? false);
  }, [user?.twoFactorEnabled]);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [twoFACode, setTwoFACode] = useState("");
  const [twoFAPassword, setTwoFAPassword] = useState("");
  const [settingUp2FA, setSettingUp2FA] = useState(false);
  const [verifying2FA, setVerifying2FA] = useState(false);
  const [disabling2FA, setDisabling2FA] = useState(false);

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      addToast({ type: "error", message: "Passwords do not match." });
      return;
    }
    if (newPassword.length < 8) {
      addToast({ type: "error", message: "Password must be at least 8 characters." });
      return;
    }
    setChangingPassword(true);
    try {
      await client.post("/auth/change-password", {
        currentPassword,
        newPassword,
      });
      addToast({ type: "success", message: "Password changed successfully." });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      addToast({
        type: "error",
        message: err.response?.data?.message || "Failed to change password.",
      });
    } finally {
      setChangingPassword(false);
    }
  }

  async function handleSetup2FA() {
    setSettingUp2FA(true);
    try {
      const res = await client.post("/auth/2fa/setup");
      setQrCodeUrl(res.data.data.qrCodeDataUrl);
    } catch (err: any) {
      addToast({
        type: "error",
        message: err.response?.data?.message || "Failed to initialize 2FA setup.",
      });
    } finally {
      setSettingUp2FA(false);
    }
  }

  async function handleVerify2FA(e: React.FormEvent) {
    e.preventDefault();
    setVerifying2FA(true);
    try {
      await client.post("/auth/2fa/enable", {
        password: twoFAPassword,
        code: twoFACode,
      });
      setTwoFAEnabled(true);
      setQrCodeUrl(null);
      setTwoFACode("");
      setTwoFAPassword("");
      updateUser({ ...user!, twoFactorEnabled: true });
      addToast({ type: "success", message: "2FA enabled successfully." });
    } catch (err: any) {
      addToast({
        type: "error",
        message: err.response?.data?.message || "Invalid code or password.",
      });
    } finally {
      setVerifying2FA(false);
    }
  }

  async function handleDisable2FA(e: React.FormEvent) {
    e.preventDefault();
    setDisabling2FA(true);
    try {
      await client.post("/auth/2fa/disable", {
        password: twoFAPassword,
        code: twoFACode,
      });
      setTwoFAEnabled(false);
      setTwoFAPassword("");
      setTwoFACode("");
      updateUser({ ...user!, twoFactorEnabled: false });
      addToast({ type: "success", message: "2FA disabled successfully." });
    } catch (err: any) {
      addToast({
        type: "error",
        message: err.response?.data?.message || "Invalid code or password.",
      });
    } finally {
      setDisabling2FA(false);
    }
  }

  if (!user) return null;

  const memberSince = new Date(user.createdAt).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="ds-max-w-3xl ds-mx-auto ds-px-4 ds-py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* User Info */}
        <div className="ds-card ds-p-8 ds-mb-8">
          <div className="ds-flex ds-items-start ds-gap-6">
            <div className="ds-w-20 ds-h-20 ds-rounded-2xl ds-bg-ds-accent/20 ds-flex ds-items-center ds-justify-center ds-text-3xl ds-font-bold ds-text-ds-accent">
              {user.username.charAt(0).toUpperCase()}
            </div>
            <div className="ds-flex-1">
              <h1 className="ds-text-2xl ds-font-bold ds-text-ds-text ds-mb-1">
                {user.displayName || user.username}
              </h1>
              {user.displayName && (
                <p className="ds-text-sm ds-text-ds-text-dim ds-mb-1">
                  @{user.username}
                </p>
              )}
              <p className="ds-text-sm ds-text-ds-text-muted ds-mb-3">
                {user.email}
              </p>
              <div className="ds-flex ds-items-center ds-gap-4 ds-text-sm ds-text-ds-text-dim">
                <span className="ds-px-2 ds-py-1 ds-rounded ds-bg-ds-bg-secondary ds-text-xs ds-font-medium">
                  {user.role}
                </span>
                <span>Member since {memberSince}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Points & Stats */}
        <div className="ds-grid ds-grid-cols-1 sm:ds-grid-cols-3 ds-gap-4 ds-mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="ds-card ds-p-6 ds-text-center"
          >
            <div className="ds-text-sm ds-text-ds-text-dim ds-mb-1">
              Points
            </div>
            <div className="ds-text-3xl ds-font-bold ds-font-mono ds-text-ds-accent">
              {(user.totalPoints ?? 0).toLocaleString()}
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="ds-card ds-p-6 ds-text-center"
          >
            <div className="ds-text-sm ds-text-ds-text-dim ds-mb-1">
              Challenges Solved
            </div>
            <div className="ds-text-3xl ds-font-bold ds-font-mono ds-text-ds-text">
              —
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="ds-card ds-p-6 ds-text-center"
          >
            <div className="ds-text-sm ds-text-ds-text-dim ds-mb-1">Rank</div>
            <div className="ds-text-3xl ds-font-bold ds-font-mono ds-text-ds-text">
              —
            </div>
          </motion.div>
        </div>

        {/* Change Password */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="ds-card ds-p-6 ds-mb-8"
        >
          <h2 className="ds-text-lg ds-font-semibold ds-text-ds-text ds-mb-4">
            Change Password
          </h2>
          <form onSubmit={handleChangePassword} className="ds-space-y-4">
            <div>
              <label className="ds-block ds-text-sm ds-font-medium ds-text-ds-text-dim ds-mb-1">
                Current Password
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="ds-input ds-w-full"
                required
                minLength={8}
              />
            </div>
            <div>
              <label className="ds-block ds-text-sm ds-font-medium ds-text-ds-text-dim ds-mb-1">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="ds-input ds-w-full"
                required
                minLength={8}
              />
            </div>
            <div>
              <label className="ds-block ds-text-sm ds-font-medium ds-text-ds-text-dim ds-mb-1">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="ds-input ds-w-full"
                required
                minLength={8}
              />
            </div>
            <button
              type="submit"
              disabled={
                changingPassword || !currentPassword || !newPassword || !confirmPassword
              }
              className="ds-btn ds-btn-primary"
            >
              {changingPassword ? (
                <span className="ds-flex ds-items-center ds-gap-2">
                  <span className="ds-w-4 ds-h-4 ds-border-2 ds-border-white ds-border-t-transparent ds-rounded-full ds-animate-spin" />
                  Changing...
                </span>
              ) : (
                "Change Password"
              )}
            </button>
          </form>
        </motion.div>

        {/* Two-Factor Authentication */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="ds-card ds-p-6"
        >
          <div className="ds-flex ds-items-center ds-justify-between ds-mb-4">
            <h2 className="ds-text-lg ds-font-semibold ds-text-ds-text">
              Two-Factor Authentication
            </h2>
            <span
              className={`ds-px-2 ds-py-1 ds-rounded ds-text-xs ds-font-medium ${
                twoFAEnabled
                  ? "ds-bg-emerald-500/20 ds-text-emerald-400"
                  : "ds-bg-ds-bg-secondary ds-text-ds-text-dim"
              }`}
            >
              {twoFAEnabled ? "Enabled" : "Disabled"}
            </span>
          </div>

          {/* Enable 2FA flow */}
          {!twoFAEnabled && !qrCodeUrl && (
            <div>
              <p className="ds-text-sm ds-text-ds-text-dim ds-mb-4">
                Add an extra layer of security to your account using an
                authenticator app.
              </p>
              <button
                onClick={handleSetup2FA}
                disabled={settingUp2FA}
                className="ds-btn ds-btn-primary"
              >
                {settingUp2FA ? (
                  <span className="ds-flex ds-items-center ds-gap-2">
                    <span className="ds-w-4 ds-h-4 ds-border-2 ds-border-white ds-border-t-transparent ds-rounded-full ds-animate-spin" />
                    Setting up...
                  </span>
                ) : (
                  "Enable 2FA"
                )}
              </button>
            </div>
          )}

          {/* QR Code + Verify */}
          {!twoFAEnabled && qrCodeUrl && (
            <div className="ds-space-y-4">
              <p className="ds-text-sm ds-text-ds-text-dim">
                Scan this QR code with your authenticator app, then enter the
                6-digit code below.
              </p>
              <div className="ds-flex ds-justify-center ds-py-4">
                <img
                  src={qrCodeUrl}
                  alt="2FA QR Code"
                  className="ds-w-48 ds-h-48 ds-rounded-lg ds-border ds-border-ds-border ds-bg-white ds-p-2"
                />
              </div>
              <form onSubmit={handleVerify2FA} className="ds-space-y-4">
                <div>
                  <label className="ds-block ds-text-sm ds-font-medium ds-text-ds-text-dim ds-mb-1">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value)}
                    className="ds-input ds-w-full ds-font-mono"
                    placeholder="000000"
                    maxLength={6}
                    pattern="\d{6}"
                    required
                  />
                </div>
                <div>
                  <label className="ds-block ds-text-sm ds-font-medium ds-text-ds-text-dim ds-mb-1">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={twoFAPassword}
                    onChange={(e) => setTwoFAPassword(e.target.value)}
                    className="ds-input ds-w-full"
                    required
                  />
                </div>
                <div className="ds-flex ds-gap-3">
                  <button
                    type="submit"
                    disabled={verifying2FA || twoFACode.length !== 6 || !twoFAPassword}
                    className="ds-btn ds-btn-primary"
                  >
                    {verifying2FA ? (
                      <span className="ds-flex ds-items-center ds-gap-2">
                        <span className="ds-w-4 ds-h-4 ds-border-2 ds-border-white ds-border-t-transparent ds-rounded-full ds-animate-spin" />
                        Verifying...
                      </span>
                    ) : (
                      "Verify & Enable"
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setQrCodeUrl(null);
                      setTwoFACode("");
                      setTwoFAPassword("");
                    }}
                    className="ds-btn ds-btn-ghost"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Disable 2FA */}
          {twoFAEnabled && (
            <div>
              <p className="ds-text-sm ds-text-ds-text-dim ds-mb-4">
                Enter your password and a code from your authenticator app to
                disable 2FA.
              </p>
              <form onSubmit={handleDisable2FA} className="ds-space-y-4">
                <div>
                  <label className="ds-block ds-text-sm ds-font-medium ds-text-ds-text-dim ds-mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    value={twoFAPassword}
                    onChange={(e) => setTwoFAPassword(e.target.value)}
                    className="ds-input ds-w-full"
                    required
                  />
                </div>
                <div>
                  <label className="ds-block ds-text-sm ds-font-medium ds-text-ds-text-dim ds-mb-1">
                    6-Digit Code
                  </label>
                  <input
                    type="text"
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value)}
                    className="ds-input ds-w-full ds-font-mono"
                    placeholder="000000"
                    maxLength={6}
                    pattern="\d{6}"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={disabling2FA || twoFACode.length !== 6 || !twoFAPassword}
                  className="ds-btn ds-btn-danger"
                >
                  {disabling2FA ? (
                    <span className="ds-flex ds-items-center ds-gap-2">
                      <span className="ds-w-4 ds-h-4 ds-border-2 ds-border-white ds-border-t-transparent ds-rounded-full ds-animate-spin" />
                      Disabling...
                    </span>
                  ) : (
                    "Disable 2FA"
                  )}
                </button>
              </form>
            </div>
          )}
        </motion.div>
      </motion.div>
    </div>
  );
}
